// A solution's own page (02-DESIGN/14-solutions/03-the-console.md § the
// page, § the frame's credential, § the handshake, § what the frame can and
// cannot do): the builder's page at its declared origin, in a sandboxed frame
// inside the shared anatomy, handed a short-lived, per-person credential the
// auth service issues for it and the console's theme tokens — never the
// console's session, its tokens or a carrier. Every message is checked
// against the declared origin and sent to it alone; the frame may say it is
// ready, show a badge, ask for a console address in the same workspace and
// say its height, and nothing else crosses.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";

import { parseAddress } from "../../address/address.ts";
import { usePage } from "../../platform/session.tsx";
import { Dot, NotAllowed, Notice, PageHead, Pill } from "../../ui/components.tsx";
import { Subjects as AuthSubjects, type AppRequest } from "../../wire/auth.ts";
import { SERVICE as AUTH } from "../../wire/identity.ts";
import {
  FrameMessage,
  pageOrigin,
  readFrameMessage,
  type CredentialMessage,
  type FrameReply,
  type Manifest,
  type Page,
  type Record_,
  type SessionMessage,
  type ThemeMessage,
} from "../../wire/solutions.ts";
import { useSurface } from "../surface.tsx";

/** THEME_TOKENS are the design system's tokens a frame is given: the colours, the type, the radii. */
export const THEME_TOKENS = [
  "--paper",
  "--card",
  "--sunken",
  "--ink",
  "--ink-2",
  "--ink-3",
  "--ink-4",
  "--border",
  "--border-field",
  "--teal",
  "--teal-soft",
  "--on-teal",
  "--green",
  "--red",
  "--blue",
  "--font",
  "--mono",
  "--text",
  "--text-small",
  "--radius",
  "--radius-card",
] as const;

/** themeOf reads the theme: the console's tokens as the document holds them, and the mode, light only (decision 0082). */
export function themeOf(doc: Document = document): ThemeMessage {
  const style = getComputedStyle(doc.documentElement);
  const tokens: Record<string, string> = {};
  for (const t of THEME_TOKENS) {
    tokens[t] = style.getPropertyValue(t).trim();
  }
  return { type: FrameMessage.theme, mode: "light", tokens };
}

/** reissueIn is when a credential is asked for again: at two-thirds of its life, never sooner than a few seconds. */
export function reissueIn(expires: string, now = Date.now()): number {
  const left = new Date(expires).getTime() - now;
  return Number.isFinite(left) ? Math.max((left * 2) / 3, 5000) : 60000;
}

/** sameWorkspace reports whether an address the frame asks for is a console address of this workspace. */
export function sameWorkspace(address: string, slug: string): boolean {
  if (!address.startsWith("/")) {
    return false;
  }
  const a = parseAddress(address.split(/[?#]/)[0] ?? "");
  return a.kind === "tenant" && a.slug === slug;
}

export function FramePage({ r, m, page, crumb }: { r: Record_; m: Manifest; page: Page; crumb: ReactNode }) {
  const s = useSurface();
  const { platform } = usePage();
  const navigate = useNavigate();
  const origin = pageOrigin(page);
  const frame = useRef<HTMLIFrameElement>(null);
  const [err, setErr] = useState<unknown>(undefined);
  const [badge, setBadge] = useState<{ count?: number; state?: string } | undefined>(undefined);
  const [height, setHeight] = useState(640);
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (origin === undefined) {
      return;
    }
    let cred: FrameReply | undefined;
    let ready = false;
    let gone = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const post = (msg: SessionMessage | ThemeMessage | CredentialMessage) => {
      frame.current?.contentWindow?.postMessage(msg, origin);
    };
    const issue = () =>
      s.bus.call<FrameReply>(AUTH, AuthSubjects.framesIssue(m.name), undefined).then((reply) => {
        cred = reply;
        if (!gone) {
          timer = setTimeout(() => {
            issue().then(
              (next) => {
                post({ type: FrameMessage.credential, credential: next.credential, expires: next.expires });
              },
              (e: unknown) => {
                setErr(e);
              },
            );
          }, reissueIn(reply.expires));
        }
        return reply;
      });
    const sendSession = () => {
      if (!ready || cred === undefined) {
        return;
      }
      post({
        type: FrameMessage.session,
        credential: cred.credential,
        expires: cred.expires,
        websocket_url: platform.websocket_url,
        sentinel: platform.sentinel,
        workspace: { slug: s.tenant, name: s.name },
        person: { display: s.who.claims?.display ?? "", role: s.role ?? "" },
        workloads: r.workloads ?? {},
      });
      post(themeOf());
      setLive(true);
    };
    issue().then(sendSession, (e: unknown) => {
      setErr(e);
    });
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== origin || ev.source !== frame.current?.contentWindow) {
        return; // heard from not at all
      }
      const msg = readFrameMessage(ev.data);
      switch (msg?.type) {
        case FrameMessage.ready:
          ready = true;
          sendSession();
          break;
        case FrameMessage.badge:
          setBadge({ count: msg.count, state: msg.state });
          break;
        case FrameMessage.navigate:
          if (sameWorkspace(msg.address, s.tenant)) {
            void navigate(msg.address);
          }
          break;
        case FrameMessage.height:
          setHeight(Math.max(msg.height, 120));
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      gone = true;
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      // Leaving the page ends the frame's credential where the service names its app.
      if (cred?.app !== undefined) {
        void s.bus
          .call(AUTH, AuthSubjects.appsRevoke(), { id: cred.app } satisfies AppRequest)
          .catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one frame per solution and origin
  }, [m.name, origin]);

  if (origin === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title={page.label} />
        <Notice kind="bad" title="Not shown">
          Its declared origin is not an https address.
        </Notice>
      </>
    );
  }
  return (
    <>
      <PageHead
        crumb={crumb}
        title={
          <span className="row">
            {page.label}
            <Dot tone={live ? "live" : "neutral"} />
            {badge?.count !== undefined && <Pill>{badge.count}</Pill>}
            {badge?.state !== undefined && (
              <Pill tone={badge.state === "live" ? "live" : badge.state === "warn" ? "bad" : "neutral"}>
                {badge.state}
              </Pill>
            )}
          </span>
        }
        line={`${m.display}'s own page, from ${origin}. It acts as you, for what its declaration lists, and nothing else.`}
      />
      {err !== undefined && <NotAllowed err={err} what="The page's credential" />}
      <iframe
        ref={frame}
        className="card"
        title={page.label}
        src={page.origin}
        sandbox="allow-scripts allow-same-origin"
        referrerPolicy="no-referrer"
        style={{ width: "100%", height, border: 0, padding: 0 }}
      />
    </>
  );
}
